import { useEffect, useState } from 'react';
import { rankingAdminApi, type RankingConfigData, type RankingPoints } from '../../../api/adminApi';
import { playerApi, type RankingResponse } from '../../../api/playerApi';
import { useAuthStore } from '../../../auth/authStore';
import { useTranslation } from '../../../i18n/useTranslation';

// ─────────────────────────────────────────────────────────────────────────────
// Página de Configuração de Ranking — /admin/ranking
//
// Permite ao admin definir:
//   1. Os pontos concedidos por cada evento de combate (vitória, morte de monstro, etc.)
//   2. O limite de jogadores exibidos no leaderboard
//   3. Se todos os jogadores são exibidos ou apenas os top N
// ─────────────────────────────────────────────────────────────────────────────

/** Array de campos para iterar na renderização com as respectivas chaves de tradução. */
const POINT_FIELDS: { key: keyof RankingPoints; allowNeg?: boolean }[] = [
  { key: 'win' },
  { key: 'loss', allowNeg: true },
  { key: 'abandonPenalty', allowNeg: true },
  { key: 'monsterKill' },
  { key: 'damagePerTen' },
  { key: 'surviveBonusHp50' },
  { key: 'surviveBonusHp80' },
  { key: 'mvpBonus' },
];

// ─────────────────────────────────────────────────────────────────────────────

export function AdminRankingPage() {
  const { t } = useTranslation();
  
  // Estado do formulário — espelha RankingConfigData
  const [config,  setConfig]  = useState<RankingConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);   // feedback de sucesso
  const [error,   setError]   = useState('');

  // Leaderboard Modal State
  const { token } = useAuthStore();
  const [showModal, setShowModal] = useState(false);
  const [leaderboard, setLeaderboard] = useState<RankingResponse | null>(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;

  // Carrega a configuração atual ao montar a página
  useEffect(() => {
    setLoading(true);
    rankingAdminApi.getConfig()
      .then(setConfig)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────

  /** Atualiza um campo de pontuação específico. */
  const setPoint = (key: keyof RankingPoints, value: number) => {
    setConfig((prev) => prev ? {
      ...prev,
      points: { ...prev.points, [key]: value },
    } : prev);
    setSaved(false);
  };

  /** Salva a configuração completa via PUT. */
  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const updated = await rankingAdminApi.updateConfig(config);
      setConfig(updated);
      setSaved(true);
      // Oculta o feedback de "salvo" após 4 segundos
      setTimeout(() => setSaved(false), 4000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const loadLeaderboard = async () => {
    if (!token) return;
    setLoadingLeaderboard(true);
    try {
      if (!showModal) setSearchQuery(''); // limpa só se estiver abrindo novo
      const data = await playerApi.getRanking(token, page, limit);
      setLeaderboard(data);
      setShowModal(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  useEffect(() => {
    if (showModal) void loadLeaderboard();
  }, [page]);

  const filteredLeaderboard = leaderboard?.leaderboard.filter(entry => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return entry.name.toLowerCase().includes(query) || entry.rank.toString() === query;
  }) || [];

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) return (
    <>
      <div className="adminHeader"><h1>{t('ranking.title')}</h1></div>
      <div className="adminContent"><div className="spinner" /></div>
    </>
  );

  if (!config) return (
    <>
      <div className="adminHeader"><h1>{t('ranking.title')}</h1></div>
      <div className="adminContent">
        <div className="alertError">{error || t('ranking.error.load')}</div>
      </div>
    </>
  );

  return (
    <>
      <div className="adminHeader">
        <h1>{t('ranking.title')}</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button 
            className="btn btnGhost" 
            onClick={loadLeaderboard}
            disabled={loadingLeaderboard}
          >
            {loadingLeaderboard ? '...' : t('ranking.viewLeaderboard')}
          </button>
          
          {/* Feedback de sucesso ao salvar */}
          {saved && (
            <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 600 }}>
              {t('ranking.success')}
            </span>
          )}
          <button className="btn btnPrimary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? t('ranking.saving') : t('ranking.saveBtn')}
          </button>
        </div>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        {/* ── Aviso informativo ── */}
        <div className="alertInfo" style={{
          background: 'rgba(68,136,255,0.08)',
          border: '1px solid rgba(68,136,255,0.25)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 24,
          fontSize: 13,
          color: 'var(--text-dim)',
        }} dangerouslySetInnerHTML={{ __html: t('ranking.info') }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

          {/* ── Coluna 1: Regras de Pontuação ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('ranking.rules.title')}</div>
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              {POINT_FIELDS.map(({ key, allowNeg }) => (
                <div key={key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                    <label className="formLabel" style={{ margin: 0, flex: 1 }}>{t(`ranking.field.${key}.label`)}</label>
                    <input
                      type="number"
                      className="formInput"
                      style={{ width: 100, textAlign: 'right' }}
                      value={config.points[key]}
                      /* loss pode ser negativo, demais >= 0 */
                      min={allowNeg ? undefined : 0}
                      max={allowNeg ? 0 : undefined}
                      onChange={(e) => setPoint(key, parseInt(e.target.value, 10) || 0)}
                    />
                    <span style={{ fontSize: 12, color: 'var(--text-dim)', minWidth: 32 }}>{t('ranking.pts')}</span>
                  </div>
                  {/* Dica contextual de cada campo */}
                  <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0, paddingLeft: 2 }}>{t(`ranking.field.${key}.hint`)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Coluna 2: Configuração de Exibição ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            <div className="adminCard">
              <div className="adminCardHeader">{t('ranking.display.title')}</div>
              <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Toggle: Mostrar todos */}
                <div>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    fontSize: 13, color: 'var(--text)',
                  }}>
                    <input
                      type="checkbox"
                      checked={config.showAllPlayers}
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                      onChange={(e) => {
                        setConfig((prev) => prev ? { ...prev, showAllPlayers: e.target.checked } : prev);
                        setSaved(false);
                      }}
                    />
                    <span dangerouslySetInnerHTML={{ __html: t('ranking.display.showAll') }} />
                  </label>
                  <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '6px 0 0 26px' }}>
                    {t('ranking.display.showAllHint')}
                  </p>
                </div>

                {/* Campo: Limite de jogadores */}
                <div style={{ opacity: config.showAllPlayers ? 0.4 : 1, transition: 'opacity 0.2s' }}>
                  <label className="formLabel">{t('ranking.display.limit')}</label>
                  <input
                    type="number"
                    className="formInput"
                    min={1}
                    max={500}
                    value={config.displayLimit}
                    disabled={config.showAllPlayers}
                    onChange={(e) => {
                      setConfig((prev) => prev ? { ...prev, displayLimit: Math.max(1, parseInt(e.target.value, 10) || 1) } : prev);
                      setSaved(false);
                    }}
                  />
                  <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
                    {t('ranking.display.limitHint')}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Info: Última atualização ── */}
            {config.updatedBy && (
              <div className="adminCard">
                <div className="adminCardHeader">{t('ranking.history.title')}</div>
                <div style={{ padding: '12px 18px', fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6 }}>
                  <div>{t('ranking.history.updatedBy')}</div>
                  <div style={{ color: 'var(--text)', fontWeight: 600 }}>{config.updatedBy}</div>
                  <div style={{ marginTop: 4 }}>
                    {new Date(config.updatedAt).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Modal Leaderboard ── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="adminCard" style={{ width: 800, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div className="adminCardHeader" style={{ fontSize: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
              <span>🏆 Leaderboard Geral</span>
              <input 
                type="text" 
                className="formInput" 
                placeholder="🔍 Buscar por nome ou posição..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ flex: 1, maxWidth: 300, fontSize: 13, padding: '4px 10px' }}
              />
              <button className="btn btnGhost" onClick={() => { setShowModal(false); setPage(1); }} style={{ marginLeft: 'auto' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: 0 }}>
              <table className="adminTable">
                <thead>
                  <tr>
                    <th style={{ width: 60, textAlign: 'center' }}>#</th>
                    <th>Nome</th>
                    <th>Classe</th>
                    <th style={{ textAlign: 'center' }}>Vitórias</th>
                    <th style={{ textAlign: 'center' }}>Derrotas</th>
                    <th style={{ textAlign: 'right' }}>Pontos</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeaderboard.map((entry) => (
                    <tr key={entry.id}>
                      <td style={{ textAlign: 'center', fontWeight: 'bold', color: 'var(--accent)' }}>
                        {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                      </td>
                      <td>{entry.name}</td>
                      <td>{entry.class}</td>
                      <td style={{ textAlign: 'center', color: 'var(--success)' }}>{entry.wins}</td>
                      <td style={{ textAlign: 'center', color: 'var(--danger)' }}>{entry.losses}</td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{entry.totalPoints}</td>
                    </tr>
                  ))}
                  {filteredLeaderboard.length === 0 && !loadingLeaderboard && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 20 }}>
                        {leaderboard?.leaderboard.length === 0 ? 'Nenhum jogador classificado.' : 'Nenhum jogador encontrado na busca.'}
                      </td>
                    </tr>
                  )}
                  {loadingLeaderboard && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 20 }}>A carregar...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {leaderboard && leaderboard.totalItems > limit && (
              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16 }}>
                <button 
                  className="btn btnGhost" 
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  « Anterior
                </button>
                <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
                  Página {page} de {Math.ceil(leaderboard.totalItems / limit)}
                </span>
                <button 
                  className="btn btnGhost" 
                  disabled={page >= Math.ceil(leaderboard.totalItems / limit)}
                  onClick={() => setPage(p => p + 1)}
                >
                  Próxima »
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
